import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Heart, Trash2, Loader2, ArrowRight } from 'lucide-react';
import { formatApiErrorDetail } from '@/utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Favorites = ({ onUsePrompt }) => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchFavorites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFavorites = async () => {
    try {
      const { data } = await axios.get(`${API}/favorites`, { withCredentials: true });
      setFavorites(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/favorites/${id}`, { withCredentials: true });
      setFavorites((prev) => prev.filter((f) => f.id !== id));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12" data-testid="favorites-loading">
        <Loader2 className="h-8 w-8 animate-spin text-amber-700" />
      </div>
    );
  }

  if (favorites.length === 0) {
    return (
      <div className="text-center py-12" data-testid="favorites-empty">
        <Heart className="h-16 w-16 mx-auto text-amber-300 mb-4" />
        <h3 className="text-lg font-medium text-amber-950 mb-2">No favorites yet</h3>
        <p className="text-amber-800/60">Tap the heart on any generated prompt to save it here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="favorites-list">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded" data-testid="favorites-error">
          {error}
        </div>
      )}
      {favorites.map((fav) => (
        <Card key={fav.id} className="hover:shadow-md transition-shadow border-amber-100" data-testid={`favorite-card-${fav.id}`}>
          <CardContent className="p-4">
            <p className="text-gray-700 mb-3 italic font-serif-italic text-lg" data-testid={`favorite-prompt-${fav.id}`}>
              &ldquo;{fav.prompt}&rdquo;
            </p>
            <div className="flex flex-wrap items-center gap-2 mb-3 text-xs text-amber-700">
              {fav.category && <span className="bg-amber-100 px-2 py-1 rounded capitalize">{fav.category}</span>}
              {fav.style && <span className="bg-orange-100 px-2 py-1 rounded capitalize">{fav.style}</span>}
            </div>
            <div className="flex space-x-2">
              <Button
                size="sm"
                onClick={() => onUsePrompt(fav.prompt)}
                className="bg-amber-900 hover:bg-amber-800 text-amber-50"
                data-testid={`use-favorite-button-${fav.id}`}
              >
                Use This Prompt
                <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDelete(fav.id)}
                className="text-red-600 hover:bg-red-50"
                data-testid={`delete-favorite-button-${fav.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default Favorites;
